import { z } from 'zod';
import { highestNumber } from './audio.js';
import { COLOUR_PALETTE } from './colour.js';
import { ASSET_NAME_MAX_CHARS } from './constants.js';
import { RoleLabel } from './media.js';
import { FOOTAGE_KINDS } from './priorities.js';
import { projectName } from './upload.js';
/** Audio character tags: applied to every clip and snippet at analysis, vital to the composer later. */
export const AUDIO_TAGS = ['voice', 'music', 'background', 'asmr', 'none'];
/** Gemini structured output for one asset. Timestamps are seconds from Gemini and are NOT cut-accurate. */
export const GeminiSegment = z.object({
    startS: z.number().min(0),
    endS: z.number().min(0),
    role: RoleLabel,
    /** A specific 2 to 4 word name for the moment ("caramel drizzle close-up"), not a generic label. */
    title: z.string().default(''),
    confidence: z.number().min(0).max(1),
    description: z.string(),
    onScreenText: z.array(z.string()).default([]),
    audioTags: z.array(z.enum(AUDIO_TAGS)).default([]),
    /** When nobody speaks: what the sound IS ("glass cracking", "spoon clanging"); empty when speech dominates. */
    soundDescription: z.string().default(''),
    /** Dominant colours on screen, primary first ("chocolate brown", "red-pink"): a description, always given. */
    colours: z.array(z.string()).default([]),
    /** Only a strong colour signature (COLOUR_SIGNATURE_RULES), primary first, at most two; usually empty. */
    colourSignature: z.array(z.string()).default([]),
    /** A person speaking or presenting straight to camera in this segment: always a scene boundary. */
    toCamera: z.boolean().default(false),
    /** The product, its packaging, a label or a logo fills the frame and holds still: a pack shot. */
    packShot: z.boolean().default(false),
    /** Edges confirmed by a windowed second pass at the source frame rate: they snap only within a few frames. */
    refined: z.boolean().default(false),
    /** The AI's own reading of the segment against the brand's lines: a primary moment, a secondary one, or neither. */
    priority: z.enum(['primary', 'secondary', 'none']).default('none'),
});
export const GeminiFacetGuess = z.object({ key: z.string(), value: z.string(), confidence: z.number().min(0).max(1) });
export const GeminiAnalysis = z.object({
    summary: z.string(),
    segments: z.array(GeminiSegment),
    facets: z.array(GeminiFacetGuess),
    language: z.string().nullable(),
    speakingFace: z.boolean(),
    /** A human-friendly short name for the clip, used to replace junk filenames. */
    suggestedName: z.string().default(''),
    /** The clip's dominant colours overall, primary first. */
    colours: z.array(z.string()).default([]),
    /** The clip's strong colour signature, when the whole clip carries one; usually empty. */
    colourSignature: z.array(z.string()).default([]),
});
/**
 * IMG_5304, DSC00012, Recording 47, sample_001: a name nobody chose and nobody can search for. The
 * audio words (recording, sample, take, bounce) joined the camera words on 2026-09-20, when a folder
 * of hand-made rips kept its names because the test only knew what a phone calls a video.
 */
/** A word a camera, phone, recorder or app puts in a filename, alone or glued to a number (IMG5304). */
const MACHINE_WORD = /^(img|dsc|dcim|mov|vid|video|rec|recording|record|screen|screenshot|screencast|capture|clip|untitled|reel|audio|aud|sound|sample|take|bounce|track|loop|new|app|voice|memo|note|download|file|output|whatsapp|ptt|at|copy|export|final|edited|trim|trimmed)\d*$/i;
export function isJunkFilename(name) {
    let base = name.replace(/\.[^.]+$/, '').trim();
    if (!base)
        return true;
    // Export prefixes and machine ids carry no meaning: "copy_", UUIDs and long hex runs (their letters
    // A to F would otherwise pass the letter-ratio test below).
    base = base
        .replace(/^(copy|export|final|edited|trim(med)?)[-_ ]+/i, '')
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '')
        .replace(/\b[0-9a-f]{8,}\b/gi, '')
        .trim();
    if (!base)
        return true;
    // Nothing but what a device or an app calls a file, numbers and dates: "App Recording 20251019 1231",
    // "New Recording 3", "download (4)", "WhatsApp Audio 2025-10-19 at 12.31.04", "DSC00012".
    const words = base.split(/[^a-z0-9]+/i).filter(Boolean);
    if (words.every((w) => /^\d+$/.test(w) || MACHINE_WORD.test(w)))
        return true;
    const letters = (base.match(/[a-z]/gi) ?? []).length;
    return letters / base.length < 0.3;
}
/**
 * A filename with the export cruft removed: extension, emoji and symbols, "Video #1" style numbering,
 * REEL / VO / copy tokens, bracketed notes like "(With VO)", underscores and dashes as spaces.
 */
export function cleanFilename(name) {
    let base = name.replace(/\.[^.]+$/, '');
    base = base
        .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, ' ')
        .replace(/\((?:with\s+)?vo\)|\[[^\]]*\]/gi, ' ')
        .replace(/\b(video|vid|clip|reel|take|version|v)\s*#?\s*\d+\b/gi, ' ')
        .replace(/^(reel|vo|copy|export|final|edited|trim(?:med)?)([-_ ]+(reel|vo|copy|export|final|edited|trim(?:med)?))*[-_ ]+/i, ' ')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return base;
}
/** How many words in a row, each opening with a capital, make a name Title Case rather than a sentence. */
const TITLE_CASE_MIN_WORDS = 3;
/**
 * An AI's name made house style: no wrapping quotes or trailing full stop, sentence case, and every word
 * of the brand's own vocabulary spelt as the brand spells it, so its people and brands keep their
 * capitals and its flavours lose a capital copied from a filename ("Pistachio"). A Title Case answer
 * ("Butterscotch Pudding Taste Test") is lowered word by word, sparing acronyms.
 */
export function tidyClipName(name, vocabulary = []) {
    let tidy = name.replace(/\s+/g, ' ').trim().replace(/^["'“‘]+|["'”’]+$/g, '').replace(/\.+$/, '').trim();
    const nouns = vocabulary.map((n) => n.trim()).filter(Boolean).sort((a, b) => b.length - a.length);
    const words = tidy.split(' ').filter((w) => /\p{L}/u.test(w));
    if (words.length >= TITLE_CASE_MIN_WORDS && words.every((w) => /^\p{Lu}/u.test(w))) {
        tidy = tidy.split(' ').map((w) => (/^\p{Lu}{2,}$/u.test(w) ? w : w.toLowerCase())).join(' ');
    }
    for (const noun of nouns) {
        const escaped = noun.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        tidy = tidy.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'giu'), noun);
    }
    return (tidy.charAt(0).toUpperCase() + tidy.slice(1)).slice(0, ASSET_NAME_MAX_CHARS);
}
/**
 * The name a clip should carry after analysis while it still wears the name it arrived with. Arrival
 * names are working notes, not titles ("gabby tatsing lemon", "plain vanilla 2"), so the AI's name wins
 * whenever there is one: the analysis was shown the filename and the brand's people and told to keep
 * their facts (Dax, 2026-09-30). Without an AI name the filename stays, cleaned of export cruft; the
 * original is kept on original_filename either way, and search still finds it.
 */
export function preferredAssetName(originalName, suggestedName, vocabulary = []) {
    const suggestion = suggestedName?.trim() ? tidyClipName(suggestedName, vocabulary) : '';
    if (suggestion)
        return suggestion;
    return cleanFilename(originalName) || originalName;
}
/**
 * Whether a clip still wears a name nobody chose: a file's own name, that name cleaned of its extension
 * and export cruft (what an earlier pass, or a pass without the AI, left it), or the stem a project took
 * from its files. Anything else a person or the AI chose, and it stays.
 */
export function isArrivalName(name, filenames) {
    const lower = name.trim().toLowerCase();
    const arrivals = [...filenames, ...filenames.map(cleanFilename), filenames.length ? projectName([...filenames]) : ''];
    return arrivals.some((a) => a.trim() !== '' && a.trim().toLowerCase() === lower);
}
/**
 * A clip name that no other clip of the brand already carries: the bare name while it is free, else the
 * next number after the highest the brand holds under it ("Spoon scoops caramel pudding 2"). Case is
 * ignored, as the Library's search ignores it.
 */
export function freeClipName(name, taken) {
    const lower = name.trim().toLowerCase();
    if (!taken.some((t) => t.trim().toLowerCase() === lower))
        return name;
    const next = Math.max(highestNumber(name, taken), 1) + 1;
    const suffix = ` ${next}`;
    return `${name.slice(0, ASSET_NAME_MAX_CHARS - suffix.length).trim()}${suffix}`;
}
export function clipOriginLines(origin, noun = 'clip') {
    if (!origin)
        return '';
    const lines = [];
    const names = [...new Set(origin.filenames.filter((f) => !isJunkFilename(f)).map(cleanFilename).filter(Boolean))];
    if (names.length) {
        const quoted = names.map((n) => `"${n}"`).join('; ');
        lines.push(`- The uploader named ${names.length === 1 ? 'the file' : `the files this ${noun} was joined from`} ${quoted}. Those words are
  the uploader's own knowledge: treat any product, flavour, variant, place or person they name as TRUE unless
  the picture plainly shows otherwise, and let them steer the facets and the suggestedName. They may be
  misspelt or abbreviated; read them as meant.`);
    }
    if (origin.people.length) {
        lines.push(`- People this brand works with: ${origin.people.join(', ')}. Name a person ONLY when the file name or
  the credit names them, matching a misspelling to the right person here; never name anyone from a face.`);
    }
    if (origin.credited.length)
        lines.push(`- This ${noun} is credited to ${origin.credited.join(' and ')}.`);
    return lines.length ? `\n${lines.join('\n')}` : '';
}
/** The dominant colours of one scene span, sampled at its midpoint frame by the worker. */
export const SceneColour = z.object({ atMs: z.number().int().min(0), hexes: z.array(z.string()) });
export const SceneKind = z.enum(['primary', 'secondary', 'other', 'passage']);
/**
 * One whole-clip scene as the worker built it: frame-exact edges, the AI segment it borrows its words
 * from, its class against the brand's priorities and its colour signature. Each lands as a
 * studio_snippets row of kind scene (its words on the row, the rest in SceneFacts), so the Analyser's
 * Video scenes tab, search and the composer all read the same index rows (Dax, 2026-09-25).
 */
export const StoredScene = z.object({
    inMs: z.number().int().min(0),
    outMs: z.number().int().min(0),
    title: z.string().nullable().default(null),
    description: z.string().nullable().default(null),
    role: z.string().nullable().default(null),
    segmentIndex: z.number().int().default(-1),
    kind: SceneKind.default('other'),
    toCamera: z.boolean().default(false),
    hexes: z.array(z.string()).default([]),
    /** One dominant colour holds from the first frame to the last. */
    colourStable: z.boolean().default(false),
    /** Sound where nobody speaks: how many spans of visual cuts the scene absorbed. */
    spans: z.number().int().min(1).default(1),
});
/** What only a scene row carries, in studio_snippets.scene: everything a StoredScene holds beyond its range and words. */
export const SceneFacts = z.object({
    sceneKind: SceneKind.default('other'),
    segmentIndex: z.number().int().default(-1),
    toCamera: z.boolean().default(false),
    hexes: z.array(z.string()).default([]),
    colourStable: z.boolean().default(false),
    spans: z.number().int().min(1).default(1),
});
export function sceneFactsOf(s) {
    return { sceneKind: s.kind, segmentIndex: s.segmentIndex, toCamera: s.toCamera, hexes: s.hexes, colourStable: s.colourStable, spans: s.spans };
}
/** How many dominant colours a scene row keeps. */
const SCENE_FACT_HEXES = 3;
/**
 * Scenes merged by hand, in time order: a highlight anywhere makes the whole one a highlight, a turn to
 * camera anywhere carries, the colour holds steady only when every part held the same one, and the
 * spans add up. The first scene lends its AI segment.
 */
export function mergeSceneFacts(list) {
    const [first] = list;
    if (!first)
        return SceneFacts.parse({});
    const kinds = list.map((f) => f.sceneKind);
    const sameColour = list.every((f) => f.colourStable && f.hexes[0] !== undefined && f.hexes[0] === first.hexes[0]);
    return {
        sceneKind: kinds.includes('primary') ? 'primary' : kinds.every((k) => k === first.sceneKind) ? first.sceneKind : kinds.includes('secondary') ? 'secondary' : 'other',
        segmentIndex: first.segmentIndex,
        toCamera: list.some((f) => f.toCamera),
        hexes: [...new Set(list.flatMap((f) => f.hexes))].slice(0, SCENE_FACT_HEXES),
        colourStable: sameColour,
        spans: list.reduce((n, f) => n + f.spans, 0),
    };
}
/** One text-only call names every spoken line in a few words; order is preserved. */
export function phraseHeadingsPrompt(lines) {
    return `These are the spoken lines of one short-form video, in order. For EACH line give a heading of
3 or 4 plain words that says what the line is about ("easy cake pop recipe", "keto and plant-based"),
no quotes, no trailing full stop. Return exactly ${lines.length} headings in the same order.
${lines.map((l, i) => `${i + 1}. ${l}`).join('\n')}`;
}
export function phraseHeadingsSchema() {
    return { type: 'object', required: ['headings'], properties: { headings: { type: 'array', items: { type: 'string' } } } };
}
/** How much of the joined summary the naming call is given; a project's gist is in its first lines. */
const PROJECT_NAME_SUMMARY_CHARS = 1500;
/**
 * Naming a project from the clips it was joined out of. Combining in the Library joins clips that were
 * each analysed already, so their names, their moments and their summaries are the material and the
 * video is never sent again: one small text call, not a second analysis (Dax, 2026-09-20). Without it
 * a project took the common stem of its files, which for a camera roll is "IMG".
 */
export function projectNamePrompt(clipNames, segmentTitles, summary) {
    return `These clips have been joined into one project, in this order. Give the project ONE short human
file name of 3 to 6 plain words saying what it shows ("Strawberry pudding taste test", "Balayage before
and after"). British English. No quotes, no file extension, no trailing full stop, no clip or part numbers.
Clips: ${clipNames.join('; ')}
${segmentTitles.length ? `Moments: ${segmentTitles.slice(0, 12).join('; ')}\n` : ''}${summary ? `What happens: ${summary.slice(0, PROJECT_NAME_SUMMARY_CHARS)}` : ''}`;
}
export function projectNameSchema() {
    return { type: 'object', required: ['name'], properties: { name: { type: 'string' } } };
}
/**
 * What makes a colour signature, for the prompt (Dax, 2026-09-30). Not an exact science: the whole frame
 * need not be one colour, but a reasonably dominant part of it must be. Mixed colours or no strong colour
 * is no signature; the plain colours still describe the scene.
 */
export const COLOUR_SIGNATURE_RULES = `A signature is one of: (1) the whole frame sits in one sympathetic part of the
  spectrum, similar hues together (light pinks, dark pinks and pale reds); (2) one strong, vibrant colour
  covering at least a fifth to a third of the frame; (3) just two strong colours dominating the frame.
  Mixed colours, muted or neutral scenes, or a colour spread thinly are NOT a signature: return an empty
  list. Name at most two colours, primary first, each exactly one of: ${COLOUR_PALETTE.join(', ')}
  (light is pale or pastel, heavy is strong or dark).`;
/** JSON schema handed to Vertex as responseSchema; the role is free text so the AI can coin classifiers. */
export function geminiAnalysisSchema() {
    return {
        type: 'object',
        required: ['summary', 'suggestedName', 'colours', 'colourSignature', 'segments', 'facets', 'language', 'speakingFace'],
        properties: {
            summary: { type: 'string' },
            suggestedName: { type: 'string' },
            language: { type: 'string', nullable: true },
            speakingFace: { type: 'boolean' },
            colours: { type: 'array', items: { type: 'string' } },
            colourSignature: { type: 'array', items: { type: 'string', enum: [...COLOUR_PALETTE] } },
            segments: {
                type: 'array',
                items: {
                    type: 'object',
                    required: ['startS', 'endS', 'role', 'title', 'confidence', 'description', 'audioTags', 'colours', 'colourSignature', 'toCamera', 'packShot', 'priority'],
                    properties: {
                        startS: { type: 'number' },
                        endS: { type: 'number' },
                        role: { type: 'string' },
                        title: { type: 'string' },
                        confidence: { type: 'number' },
                        description: { type: 'string' },
                        onScreenText: { type: 'array', items: { type: 'string' } },
                        audioTags: { type: 'array', items: { type: 'string', enum: [...AUDIO_TAGS] } },
                        soundDescription: { type: 'string' },
                        colours: { type: 'array', items: { type: 'string' } },
                        colourSignature: { type: 'array', items: { type: 'string', enum: [...COLOUR_PALETTE] } },
                        toCamera: { type: 'boolean' },
                        packShot: { type: 'boolean' },
                        priority: { type: 'string', enum: ['primary', 'secondary', 'none'] },
                    },
                },
            },
            facets: {
                type: 'array',
                items: {
                    type: 'object',
                    required: ['key', 'value', 'confidence'],
                    properties: { key: { type: 'string' }, value: { type: 'string' }, confidence: { type: 'number' } },
                },
            },
        },
    };
}
/**
 * The facets instruction. With the brand's categories the AI chooses from the brand's own words, so a
 * confident pick lands as that value (banana under Pudding Flavour) instead of a near-miss it coined;
 * whatever fits no category comes back under its own key and stays custom to the clip.
 */
export function facetsLine(categories) {
    const listed = (categories ?? []).filter((c) => c.values.length > 0);
    if (!listed.length)
        return '- facets: product, flavour, setting or mood attributes you can see, as key/value guesses with confidence.';
    return `- facets: key/value attributes with a confidence from 0 to 1. The brand files its clips under these
  categories; for each one that applies, choose the value(s) you can see or hear FROM ITS LIST, giving the
  category key and the value exactly as written:
${listed.map((c) => `    - key "${c.key}" (${c.label}): ${c.values.join(', ')}`).join('\n')}
  Never invent a value for a listed category. Give confidence 0.8 or more only when the clip plainly shows
  it (the packaging names it, or it is unmistakable on screen). Then add any other product, flavour, setting
  or mood attributes you can see that no category covers, each under a short lowercase key of your own.`;
}
export function geminiAnalysisPrompt(roles, instruction, variation, priorities, categories, origin) {
    const briefLine = !instruction ? prioritiesLine(priorities ?? null) : '';
    const variationLine = variation && !instruction
        ? `\nThis clip has been analysed before. Take a FRESH angle this time: where reasonable, choose
different segment boundaries and alternative moments, and phrase classifiers and descriptions
differently, rather than reproducing the previous result.`
        : '';
    const segmentsLine = instruction
        ? `- segments: ONLY the moments matching this instruction, nothing else: "${instruction}".
  Cover the whole clip looking for matches; return an empty list if nothing matches. For each give
  startS and endS in seconds from the start of the clip, a role, a confidence from 0 to 1, a
  description, and any on-screen text you can read.`
        : `- segments: the clip's COMPLETE logical scenes. A segment is an action from its natural start to its
  natural end, response included: a demonstration runs from the first move to the reaction that
  follows it, whatever the subject (food, hair, fitness, a product unboxing). Judge variation across
  environment, subject, camera angle, movement, pace and colour. Never fragment one continuous scene;
  a SHORT single-scene clip gets ONE segment covering it, trimmed of dead lead-in and lead-out. But a
  LONG take of one continuous scene must still be offered in usable pieces: return its best distinct
  beats, roughly one every 30 to 45 seconds, each a complete action. A repeated action (two separate
  demonstrations) is separate segments. A long-form video gets as many segments as it has genuine
  scenes. Consecutive preparatory steps of the same process in the same setting (successive ingredients,
  successive strokes, successive reps) are ONE segment, not one per step, unless the brand's own lines
  below name them separately; a pack shot or a turn to camera among them still stands apart. Together the segments should COVER the clip: where footage sits
  between two scenes, extend a neighbouring segment or add a short connective segment at lower
  confidence rather than leaving that stretch undescribed. For each give startS and endS in seconds
  from the start of the clip, a role, a confidence from 0 to 1, a description, and any on-screen
  text you can read.${briefLine}`;
    return `You are indexing a short-form video clip from a content creator for a brand's video library.
Watch the clip and return:
- summary: one or two sentences on what happens.
- suggestedName: a short human name for the clip, 3 to 6 plain words in sentence case saying what it shows
  (e.g. "Strawberry pudding taste test", "Balayage before and after"). Whenever they are known, from the file
  name, the credit or the packaging, include the person's name and the product or flavour, the file name's
  words spelt correctly and in full. British English. No quotes, no file extension, no numbers.${clipOriginLines(origin)}
- colours: the clip's dominant colours overall, primary FIRST then secondary, as specific colour names
  ("chocolate brown", "red-pink", "mint green"). This describes the colours; always give it.
- colourSignature: the clip's colour signature, ONLY when the whole clip carries a strong one; otherwise
  an empty list. ${COLOUR_SIGNATURE_RULES}
${segmentsLine}
  Also per segment:
  - title: a specific 2 to 4 word name for the moment ("caramel drizzle close-up", "pack reveal"), never a generic label.
  - audioTags: which of voice, music, background, asmr, none characterise its sound; include a tag only
    when you are at least 50% sure.
  - soundDescription: when nobody speaks in the segment, 3 to 8 plain words naming the sounds heard
    ("glass cracking", "spoon clanging on ceramic", "soft room ambience"), deduced from the sound and
    the picture together. An empty string when speech dominates.
  - colours: the segment's dominant colours, primary first then secondary; always give it.
  - colourSignature: the segment's colour signature, ONLY when it has a strong one; otherwise an empty
    list. Most segments have none. Same test as the clip's.
  - toCamera: true when a person speaks or presents straight to camera in the segment. A turn to camera
    is ALWAYS a new segment: never let it share a segment with what came before or after.
  - packShot: true when the product, its packaging, a label or a logo fills the frame and holds still,
    whatever the industry (a box, a bottle, a jar, a garment on a hanger, a signed card). A pack shot is
    ALWAYS its own segment, even inside a run of steps: never let it share a segment with what came
    before or after.
  - priority: "primary" when the segment IS one of the brand's PRIMARY moments listed below, "secondary"
    when it is one of the SECONDARY ones, otherwise "none". Judge the moment, not a shared word: a spoon
    merely in shot is not the spoon-dragging moment; a bowl of mixture is not the finished product.
  role: one or two lowercase words that SPECIFICALLY classify the scene. Prefer one of the brand's
  existing classifiers (${roles.join(', ')}) when it genuinely fits; otherwise coin a new one
  ("model posing", "behind the scenes", "pack shot", "client reaction", "pour", "blow dry"). NEVER
  answer "broll", "other" or any generic filler: every scene has a more specific name than that.
  description: a compact, useful 3 to 10 word account of what is visible, e.g. "spoon digs into layered
  pink dessert", "stylist sections wet hair at the mirror" or "pack shot with the flavour name". No
  marketing copy.
${facetsLine(categories)}
- language: the BCP-47 code of any spoken language, or null if nobody speaks.
- speakingFace: true if a person speaks to camera at any point.
Give startS and endS to the nearest tenth of a second, and where a moment begins or ends on a visual change put
the timestamp ON that change; cut points are then refined to the exact frame.${variationLine}`;
}
/**
 * What one Gemini image call returns for a still (Dax, 2026-10-05): the clip analysis without time or sound.
 * `subject` describes whoever or whatever the photo is about in words that would recognise it again in the
 * same shoot's other photos and clips; it is the label a subject is first known by.
 */
export const StillAnalysis = z.object({
    summary: z.string(),
    suggestedName: z.string().default(''),
    description: z.string().default(''),
    subject: z.string().default(''),
    colours: z.array(z.string()).default([]),
    colourSignature: z.array(z.string()).default([]),
    facets: z.array(GeminiFacetGuess).default([]),
});
export function stillAnalysisSchema() {
    return {
        type: 'object',
        required: ['summary', 'suggestedName', 'description', 'subject', 'colours', 'colourSignature', 'facets'],
        properties: {
            summary: { type: 'string' },
            suggestedName: { type: 'string' },
            description: { type: 'string' },
            subject: { type: 'string' },
            colours: { type: 'array', items: { type: 'string' } },
            colourSignature: { type: 'array', items: { type: 'string', enum: [...COLOUR_PALETTE] } },
            facets: {
                type: 'array',
                items: {
                    type: 'object',
                    required: ['key', 'value', 'confidence'],
                    properties: { key: { type: 'string' }, value: { type: 'string' }, confidence: { type: 'number' } },
                },
            },
        },
    };
}
/** The prompt sent with stillAnalysisSchema: the brand's categories and the photo's origin, nothing else. */
export function stillAnalysisPrompt(categories, origin) {
    return `You are indexing a still photograph for a brand's media library; it may later be animated into a short clip.
Look at the photo and return:
- summary: one sentence on what the photo shows.
- suggestedName: a short human name for the photo, 3 to 6 plain words in sentence case saying what it shows
  (e.g. "Balayage waves from behind", "Glazed tart on marble", "Detailed bonnet after polish"). Whenever they are
  known, from the file name, the credit or the packaging, include the person's name and the product. British
  English. No quotes, no file extension, no numbers.${clipOriginLines(origin, 'photo')}
- description: a compact 5 to 15 word account of what is visible. No marketing copy.
- subject: the photo's main subject in 3 to 8 plain words that would recognise the same subject in the other
  photos and clips of the same shoot ("long brunette layered waves", "matte black hatchback", "three-tier white
  wedding cake"). Describe only what is visible; never guess a name, an age or an ethnicity.
- colours: the photo's dominant colours, primary FIRST then secondary, as specific colour names ("chocolate
  brown", "red-pink", "mint green"). Always give it.
- colourSignature: the photo's colour signature, ONLY when it carries a strong one; otherwise an empty list.
  ${COLOUR_SIGNATURE_RULES}
${facetsLine(categories)}`;
}
/**
 * The brand's own lines, as the paragraph the analysis prompt carries. Only the nature of this brand's
 * footage goes here; every rule about how scenes split and join is in the neutral prompt above.
 */
export function prioritiesLine(p) {
    if (!p)
        return '';
    const parts = [];
    const direction = p.direction.trim();
    if (direction) {
        parts.push(`  The brand's own direction for its footage, in its words; follow it wherever it applies:
    ${direction.replace(/\s*\n\s*/g, '\n    ')}`);
    }
    if (p.primaryShots.length) {
        parts.push(`  PRIMARY moments for this brand, most important first. Whenever one is present make it its own segment
  from its natural start to its natural end and give it confidence 0.8 or more:
${p.primaryShots.map((l) => `    - ${l}`).join('\n')}`);
    }
    if (p.secondaryShots.length) {
        parts.push(`  SECONDARY moments, worth indexing at confidence 0.6 or less; consecutive secondary steps that look
  alike belong in ONE segment:
${p.secondaryShots.map((l) => `    - ${l}`).join('\n')}`);
    }
    const kind = p.footageKey && p.footageKey !== 'custom' ? FOOTAGE_KINDS.find((k) => k.key === p.footageKey) : null;
    const range = p.footageKey === 'custom' && p.footageMinS && p.footageMaxS ? `${p.footageMinS} to ${p.footageMaxS} s` : kind ? `${kind.minS} to ${kind.maxS} s` : null;
    if (range)
        parts.push(`  This brand's clips usually run ${range}${kind?.key === 'longform' ? '; long takes are offered as passages of 30 to 60 s, each a complete thought' : ''}.`);
    return parts.length ? `\n${parts.join('\n')}` : '';
}
/**
 * The second pass: one short excerpt, sampled at the source frame rate, and one moment to place exactly.
 * Times are asked for relative to the excerpt so the model never has to reason about the clip's clock.
 */
export function refineSegmentPrompt(moment, excerptS) {
    return `This is an excerpt ${excerptS.toFixed(2)} seconds long from a longer clip, sampled densely. Somewhere in it is this moment:
"${moment.title || moment.role}": ${moment.description}.
Give, in seconds from the START OF THIS EXCERPT and to two decimals:
- startS: the first sampled frame in which the moment is on screen (the shot it belongs to has begun).
- endS: the first sampled frame after the moment, once the picture has changed to what follows.
If the moment is only partly inside the excerpt, give the edge you can see and 0 or ${excerptS.toFixed(2)} for the other.`;
}
export function refineSegmentSchema() {
    return { type: 'object', required: ['startS', 'endS'], properties: { startS: { type: 'number' }, endS: { type: 'number' } } };
}
export const RefinedEdges = z.object({ startS: z.number(), endS: z.number() });
/** The default aim of an analysis: about this many kept snippets per clip unless the toolbar says otherwise. */
export const DEFAULT_TARGET_SNIPPETS = 6;
/** The numeric analyse settings that aggregate into per-brand defaults. */
export const ANALYSE_NUMERIC_FIELDS = ['minSnippets', 'maxSnippets', 'minSnippetMs', 'maxSnippetMs', 'sceneThreshold'];
/**
 * Rolling sums of what the brand actually sets, so defaults drift towards its habits. Parsed, never
 * cast: the column defaults to an empty object, which carries neither map.
 */
export const AnalyseAggregate = z.object({
    counts: z.record(z.enum(ANALYSE_NUMERIC_FIELDS), z.number()).default({}),
    sums: z.record(z.enum(ANALYSE_NUMERIC_FIELDS), z.number()).default({}),
});
export function foldAnalyseAggregate(agg, params) {
    const next = { counts: { ...agg?.counts }, sums: { ...agg?.sums } };
    for (const f of ANALYSE_NUMERIC_FIELDS) {
        const v = params[f];
        if (v === null || v === undefined)
            continue;
        next.counts[f] = (next.counts[f] ?? 0) + 1;
        next.sums[f] = (next.sums[f] ?? 0) + v;
    }
    return next;
}
/** Per field: the brand's explicit default wins, else the average of what it usually sets. */
export function effectiveAnalyseDefaults(explicit, agg) {
    const out = {};
    for (const f of ANALYSE_NUMERIC_FIELDS) {
        const set = explicit?.[f];
        if (set !== null && set !== undefined) {
            out[f] = set;
            continue;
        }
        const n = agg?.counts[f] ?? 0;
        const sum = agg?.sums[f] ?? 0;
        if (n > 0)
            out[f] = f === 'sceneThreshold' ? Number((sum / n).toFixed(2)) : Math.round(sum / n);
    }
    return out;
}
export const Word = z.object({ idx: z.number().int(), word: z.string(), startMs: z.number().int(), endMs: z.number().int(), confidence: z.number().min(0).max(1) });
export const Region = z.object({ kind: z.enum(['speech', 'noise', 'quiet']), startMs: z.number().int(), endMs: z.number().int() });
export const ProbeResult = z.object({
    durationMs: z.number().int(),
    /** The video stream's own duration; audio often runs a little longer (AAC padding), which matters at a join. */
    videoDurationMs: z.number().int(),
    /** The camera's recording time from the container tags, when it carries one (ISO 8601). */
    creationTime: z.string().nullable(),
    width: z.number().int(),
    height: z.number().int(),
    fps: z.number(),
    sampleRate: z.number().int().nullable(),
    channels: z.number().int().nullable(),
    videoCodec: z.string().nullable(),
    audioCodec: z.string().nullable(),
    bytes: z.number().int(),
});
/**
 * A brand's speech defaults (Dax, 2026-10-06), kept with its analysis defaults. Off: a clip past LONG_CLIP_MS
 * is analysed without its speech transcribed, which is a salon's half hour of chatter nobody will use, and a
 * person transcribes the one that needs it from its menu.
 */
export const SpeechDefaults = z.object({ transcribeLong: z.boolean().default(false) });
/** The Analyse button's parameters; stored on the asset so a re-analysis repeats the same choices. */
export const AnalyseParams = z.object({
    minSnippets: z.number().int().min(1).max(30).nullable().default(null),
    maxSnippets: z.number().int().min(1).max(30).nullable().default(null),
    /**
     * auto transcribes speech, except in a clip past LONG_CLIP_MS when the brand has not asked for that
     * (transcribesSpeech); always transcribes whatever the length; none skips it for music-only or b-roll clips.
     */
    voice: z.enum(['auto', 'always', 'none']).default('auto'),
    sceneThreshold: z.number().min(0.1).max(0.9).nullable().default(null),
    minSnippetMs: z.number().int().min(100).max(30000).nullable().default(null),
    maxSnippetMs: z.number().int().min(200).max(60000).nullable().default(null),
    /** true runs Gemini for classifiers and descriptions; false is the free, script-only segmentation. */
    ai: z.boolean().default(true),
    /** A free instruction: segment ONLY what matches it ("every part with a face"). Implies the AI pass. */
    prompt: z.string().max(300).nullable().default(null),
    /** Brought in without analysis (Dax, 2026-10-31): a quick preview and its keyframes, ready for Composer Fast, and no more. */
    quick: z.boolean().default(false),
    /** A repeat auto-analysis looks for a fresh angle instead of reproducing the previous result. */
    variation: z.boolean().default(false),
    /** A person said yes to analysing a clip past LONG_CLIP_MS; it is held for them otherwise. */
    confirmLong: z.boolean().default(false),
});
export const SnippetProposal = z.object({
    role: RoleLabel,
    inMs: z.number().int(),
    outMs: z.number().int(),
    confidence: z.number().min(0).max(1),
    description: z.string(),
    boundaryScore: z.number().min(0).max(1),
    /** How the brand's lines class this moment; highlights are primaries first. */
    priority: z.enum(['primary', 'secondary', 'other']).optional(),
});
//# sourceMappingURL=analysis.js.map